import { getAuthToken } from "../auth/teams";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(status: number, code: string | undefined, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  } else if (import.meta.env.VITE_DEV_USER) {
    // Local-dev mode: backend must have DEV_BYPASS_AUTH=1
    headers.set("x-dev-user", import.meta.env.VITE_DEV_USER);
    if (import.meta.env.VITE_DEV_NAME) headers.set("x-dev-name", import.meta.env.VITE_DEV_NAME);
  }
  if (!(init.body instanceof FormData) && init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try { payload = await res.json(); } catch { /* not json */ }
    throw new ApiError(res.status, payload.error?.code, payload.error?.message ?? res.statusText, payload.error?.details);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json() as Promise<T>;
  return (await res.text()) as unknown as T;
}

async function download(path: string, suggestedName?: string): Promise<void> {
  const token = await getAuthToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  else if (import.meta.env.VITE_DEV_USER) {
    headers.set("x-dev-user", import.meta.env.VITE_DEV_USER);
    if (import.meta.env.VITE_DEV_NAME) headers.set("x-dev-name", import.meta.env.VITE_DEV_NAME);
  }
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string } } = {};
    try { payload = await res.json(); } catch { /* not json */ }
    throw new ApiError(res.status, payload.error?.code, payload.error?.message ?? res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  if (suggestedName) a.download = suggestedName;
  // Fall back to a new tab if download is blocked
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const api = {
  get:    <T>(p: string)            => request<T>(p),
  post:   <T>(p: string, body?: unknown) => request<T>(p, { method: "POST", body: body instanceof FormData ? body : JSON.stringify(body ?? {}) }),
  patch:  <T>(p: string, body?: unknown) => request<T>(p, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
  del:    <T>(p: string)            => request<T>(p, { method: "DELETE" }),
  download,
};
